import moment from "moment";
import Icon from "@mdi/react";
import { useEffect, useMemo, useState } from "react";
import { mdiPlus, mdiStar, mdiStarOutline } from "@mdi/js";
import Modal from "react-responsive-modal";
import { Link, useRouter, useTitle } from "easy-page-router/react";

import { useHeaders, useSSRHook } from "../../ssrh/client";
import { useUser } from "../User";
import { ContentSplit, Header, Loading } from "../components";
import { api } from "../services/api";

import { Project } from "../../types";
import { groupBy } from "lodash";
import Footer from "../components/Footer";

const emptyProject: Project = {
    _id: "",
    title: "",
    url: "",
    date: moment().format("YYYY-MM-DD"),
    description: "",
    content: "",
    files: [],
    gallery: [],
};

export default function Projects() {
    const user = useUser();
    const { push, searchParams } = useRouter();

    const [projects, error, isLoading, reload] = useSSRHook<Project[]>("/api/projects");

    const title = "Projekty | TSO Krkonoše";
    useTitle(title);
    useHeaders({
        title,
        description: "Seznam projektů technického svazu obcí Krkonoše",
        image: window.location.origin + "/logo.png",
        canonical: window.location.origin + window.location.pathname,
    });

    const [newProject, setNewProject] = useState<null | Project>(null);
    const [message, setMessage] = useState("");

    const projectGroup = useMemo(() => {
        if (!projects) return { [moment().format("YYYY")]: [] };
        return groupBy(projects || [], p => moment(p.date).format("YYYY"));
    }, [projects]);

    const [year, setYear] = useState(searchParams.rok || Object.keys(projectGroup).reverse()[0]);

    // set last year with new data
    useEffect(() => {
        setYear(searchParams.rok || Object.keys(projectGroup).reverse()[0]);
    }, [projects]);

    async function handleAddProject() {
        if (!newProject) return;

        const [project] = await api.addProject(newProject);

        if (!project) {
            setMessage("Přidání projektu selhalo");
            return;
        }

        reload();
        setNewProject(null);
        setMessage("Projekt úspěšně přidán");

        push(`/projekt/${encodeURIComponent(project.url)}`);
    }

    return (
        <>
            <main>
                <Header background="pens" title="Projekty">
                    <Link to="/o-nas">O nás</Link>
                    <span className="space">-</span>
                    Projekty
                </Header>
                <ContentSplit
                    left={<>
                        <h1 className="hidden">Projekty</h1>

                        {user.isAdmin && (
                            <div className="admin">
                                <button onClick={() => setNewProject(emptyProject)}>
                                    <Icon path={mdiPlus} size={1} />Přidat
                                    projekt
                                </button>
                                <Modal open={newProject !== null} onClose={() => setNewProject(null)}>
                                    <h2>Přidat projekt</h2>
                                    {newProject && <>
                                        <input
                                            type="text"
                                            placeholder="Název projektu"
                                            value={newProject.title}
                                            onChange={e => setNewProject({ ...newProject, title: e.target.value })}
                                        />
                                        <input
                                            type="date"
                                            placeholder="Datum"
                                            value={newProject.date}
                                            onChange={(e) => setNewProject({
                                                ...newProject,
                                                date: moment(e.target.value).format("YYYY-MM-DD"),
                                            })}
                                        />
                                        <button onClick={handleAddProject}>Přidat</button>
                                    </>}
                                </Modal>
                            </div>
                        )}

                        {isLoading && <Loading />}
                        {error && (
                            <p className="alert">
                                Projekty se bohužel nepodařili načíst 🙁
                            </p>
                        )}
                        {projects && projects.length === 0 && <div className="alert">Nejsou zde žádné projekty</div>}

                        {user.isAdmin && message && <div className="alert">{message}</div>}

                        {projects && (
                            <div className="list">
                                {projectGroup[year]?.map((p) => (
                                    <div key={p._id} className="item">
                                        <div className="date">
                                            <div className="content">
                                                <div className="day">
                                                    {moment(p.date).format("D")}
                                                </div>
                                                <div className="month">
                                                    {moment(p.date).format(
                                                        "MMMM",
                                                    )}
                                                </div>
                                                <div className="year">
                                                    {moment(p.date).format(
                                                        "YYYY",
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="content">
                                            <h2><Link to={`/projekt/${encodeURIComponent(p.url)}`}>{p.title}</Link></h2>
                                            <p>{p.description}</p>
                                        </div>
                                        <div className="more">
                                            <Link to={`/projekt/${encodeURIComponent(p.url)}`}>
                                                Více
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>}
                    right={<>
                        {isLoading && <Loading />}
                        {projects && <ul className="cat-list">
                            {Object.keys(projectGroup).length === 0 && <li className="current"><a>
                                <Icon path={mdiStar} size={0.8} />
                                Aktuální
                            </a></li>}
                            {Object.keys(projectGroup)
                                .reverse()
                                .map(y => <li key={y} className={y === year ? "current" : ""}>
                                    <a onClick={() => setYear(y)}>
                                        <Icon path={y === year ? mdiStar : mdiStarOutline} size={0.8} />
                                        {y}
                                    </a>
                                </li>)}
                        </ul>}
                    </>}
                />
            </main>
            <Footer />
        </>
    );
}
